import React, { useState, useRef, useEffect } from 'react';
import { useQuery } from '@apollo/react-hooks';

import './desktop-exhibition-style.scss';
import useIsComDomain from '@src/hooks/useIsComDomain';

import { NoResult } from '@src/components/molecules';
import Typography from '@src/componentsNew/Typography/Typography';
import ProgressiveImg from '@src/componentsNew/Image/ProgressiveImg';
import ProductListTitle from '@src/componentsNew/ProductListTitle/ProductListTitle';
import VerticalProductThumbnail from '@src/componentsNew/ProductThumbnail/VerticalProductThumbnail/VerticalProductThumbnail';
import DesktopProductListSkeleton from '@src/componentsNew/Skeleton/desktop/components/DesktopProductListSkeleton';

import { exhibition } from './__generated__/exhibition';
import { EXHIBITION } from './queries';

import { getDBIdFromGraphqlId, getGraphqlIdFromDBId } from '@src/core/utils';
import { makeCollectionUrl } from '@src/utils';
import { PLACEHOLDER_IMAGE_URL } from '@src/utils/constants';

const collectionCount = 1;
const collectionProductCount = 12;
const sortableProductCount = 30;

const DesktopExhibitionPage = ({ match }) => {
  const isComDomain = useIsComDomain();
  const exhibitionId = getGraphqlIdFromDBId(match.params.id, 'Exhibition');

  const fetchingRef = useRef(false);
  const hasNextPageRef = useRef(false);
  const lastSectionElementRef = useRef(null);
  const [fetchMoreLoading, setFetchMoreLoading] = useState(false);

  const [isPlaceholderImage, setIsPlaceholderImage] = useState(false);

  const variables = {
    id: exhibitionId,
    collectionCount,
    collectionProductCount,
    sortableProductCount,
    isMobile: false,
    ...(isComDomain && { isMgs: true }),
  };

  const { data, loading, fetchMore } = useQuery<exhibition>(EXHIBITION, {
    fetchPolicy: 'network-only',
    variables,
    onCompleted: (data) => {
      hasNextPageRef.current = data?.exhibition?.sections?.pageInfo?.hasNextPage;
      setIsPlaceholderImage(data.exhibition?.image?.url.includes(PLACEHOLDER_IMAGE_URL));
    },
  });

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasNextPageRef.current && !fetchingRef.current) {
        setFetchMoreLoading(true);

        fetchMore({
          variables: {
            ...variables,
            after: data?.exhibition.sections.pageInfo.endCursor,
          },
          updateQuery: (previousResult, { fetchMoreResult }) => {
            const newEdges = fetchMoreResult?.exhibition.sections.edges;
            const pageInfo = fetchMoreResult?.exhibition.sections.pageInfo;

            hasNextPageRef.current = pageInfo?.hasNextPage;
            fetchingRef.current = false;
            setFetchMoreLoading(false);

            return newEdges.length
              ? {
                  exhibition: {
                    ...previousResult.exhibition,
                    sections: {
                      __typename: previousResult.exhibition.sections.__typename,
                      edges: [...previousResult.exhibition.sections.edges, ...newEdges],
                      pageInfo,
                    },
                  },
                }
              : previousResult;
          },
        });
      }
    });

    if (lastSectionElementRef?.current) {
      observer.observe(lastSectionElementRef?.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [data]);

  if (loading) {
    return (
      <div style={{ marginTop: '120px' }}>
        <DesktopProductListSkeleton type="horizontal" count={12} hasTitle={true} hasTitleRightArea={true} />;
      </div>
    );
  }

  return data?.exhibition ? (
    <section id="desktop-exhibition-container">
      <div className={`exhibition-content-wrapper ${isPlaceholderImage ? 'g-32' : ''}`}>
        <div className={`exhibition-image-area ${isPlaceholderImage ? 'left' : 'center'}`}>
          <Typography variant="h4" component="h2" style={{ color: '#212121' }}>
            {data?.exhibition.name}
          </Typography>

          {!isPlaceholderImage && (
            <ProgressiveImg
              key={data.exhibition.image?.url}
              originImg={data.exhibition.image?.url}
              placeholder={data.exhibition.placeholderImage?.url}
              alt={data.exhibition.image?.alt || ''}
            />
          )}
        </div>

        {data.exhibition.sections?.edges?.length ? (
          <div className="exhibition-section-wrapper">
            {data.exhibition.sections.edges.map((section, idx) => {
              if (
                (section?.node?.sectionType === 'COLLECTION' &&
                  section?.node?.collection?.products?.edges?.length > 0) ||
                (section?.node?.sectionType === 'PRODUCT' && section?.node?.sortableProducts?.edges?.length > 0)
              ) {
                const isLastSection = idx === data.exhibition.sections.edges.length - 1;

                return (
                  <div
                    key={section.node.id}
                    className="exhibition-section"
                    ref={isLastSection ? lastSectionElementRef : null}
                  >
                    <ProductListTitle
                      title={section?.node?.name}
                      typoVariant="h6"
                      typoComponent="h4"
                      isShowBottomLine={true}
                      isShowRightBtn={
                        section.node.sectionType === 'COLLECTION' &&
                        collectionProductCount < section?.node?.collection?.products?.totalCount
                      }
                      url={
                        section.node.sectionType === 'COLLECTION'
                          ? makeCollectionUrl(
                              section?.node?.collection?.slug,
                              getDBIdFromGraphqlId(section?.node?.collection?.id).toString(),
                            )
                          : ''
                      }
                      rightBtnColor="#11141D99"
                      isShowArrow={true}
                    />

                    <div className="desktop-exhibition-products-thumbnail-wrapper">
                      {section?.node?.sectionType === 'COLLECTION' &&
                        section?.node?.collection?.products?.edges?.map((edge) => (
                          <VerticalProductThumbnail key={edge.node.id} product={edge.node} isVerticalOne={true} />
                        ))}

                      {section?.node?.sectionType === 'PRODUCT' &&
                        section?.node?.sortableProducts?.edges?.map((edge) => (
                          <VerticalProductThumbnail
                            key={edge.node.id}
                            product={edge.node.product}
                            isVerticalOne={true}
                            isAward={true}
                            rank={edge.node.sortOrder + 1}
                          />
                        ))}
                    </div>
                  </div>
                );
              } else {
                return null;
              }
            })}

            {fetchMoreLoading && (
              <DesktopProductListSkeleton type="horizontal" count={12} hasTitle={true} hasTitleRightArea={true} />
            )}
          </div>
        ) : null}
      </div>
    </section>
  ) : (
    <div className="no-result-wrapper">
      <NoResult />
    </div>
  );
};

export default DesktopExhibitionPage;
